import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import WorkflowDashboard from '../components/WorkflowDashboard';
import Dashboard from '../pages/Dashboard';
import Module1AnalysisWrapper from '../pages/Module1Analysis';
import Module2CodeGeneration from '../pages/Module2CodeGeneration';
import Module2Classification from '../pages/Module2Classification';
import Module3SemanticAnalysis from '../pages/Module3SemanticAnalysis';
import Module4Config from '../pages/Module4Config';
import Module5JobMonitoring from '../pages/Module5JobMonitoring';
import Layout from '../components/Layout';
import Login from '../pages/Login';
import AuthGuardRedirect from '../pages/AuthGuardRedirect';
import ClientSelection from '../pages/ClientSelection';
import ApiDebugPage from '../pages/ApiDebugPage';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../context/AuthContext';
import AuthGuard from '../components/AuthGuard';
import StartProcess from '@/pages/StartProcess';
import ClientKeywordSetupScreen from '../pages/ClientKeywordSetupScreen';
import FileProgressAuditDemo from '../pages/FileProgressAuditDemo';
import WorkflowRedirect from '../components/WorkflowRedirect';
import TestAnalysisResults from '../components/TestAnalysisResults';
import DebugAnalysisResults from '../components/DebugAnalysisResults';
import SerpTool from '../pages/SerpTool';
import DataForSeoTool from '../pages/DataForSeoTool';
import InteractiveReport from '../pages/InteractiveReport';
import Explore from '../pages/Explore';
import ExploreGate from '../pages/ExploreGate';
import SamplingStrategyDemo from '../pages/SamplingStrategyDemo';
import SamplingComparisonDemo from '../pages/SamplingComparisonDemo';
import SamplingDemoLanding from '../pages/SamplingDemoLanding';
// Lazy load Qlik components to avoid unnecessary initialization
// import QlikTestPage from '../components/QlikTestPage';
// import MinimalQlikTest from '../components/MinimalQlikTest';
import UserTest from '../pages/UserTest';
import Manage from '../pages/Manage';
import UserManagement from '../pages/UserManagement';
import ApplicationSettings from '../pages/ApplicationSettings';
import WorkflowV2Layout from '../pages/workflow-v2';
import FileUploadStep from '../pages/workflow-v2/FileUploadStep';
import KeywordAnalysisStep from '../pages/workflow-v2/KeywordAnalysisStep';
import SemanticAnalysisStep from '../pages/workflow-v2/SemanticAnalysisStep';
import BatchConfigStep from '../pages/workflow-v2/BatchConfigStep';
import BatchRunStep from '../pages/workflow-v2/BatchRunStep';
import SerpToolStep from '../pages/workflow-v2/SerpToolStep';
import DataForSeoStep from '../pages/workflow-v2/DataForSeoStep';
import WorkflowSummaryStep from '../pages/workflow-v2/WorkflowSummaryStep';
import ExploreStep from '../pages/workflow-v2/ExploreStep';

// Error component to display when routes encounter errors
const ErrorComponent = () => {
  return (
    <div className="p-6 bg-red-50 border border-red-200 rounded-md">
      <h2 className="text-xl font-bold text-red-600 mb-2">An error occurred</h2>
      <p className="text-gray-700">
        We're sorry, but something went wrong. Please try refreshing the page or contact support if the problem persists.
      </p>
      <button 
        onClick={() => window.location.href = '/'}
        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
      >
        Return to Dashboard
      </button>
    </div>
  );
};

// Protected Route wrapper using AuthGuard
const ProtectedRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  return (
    <AuthGuard>
      <>{element}</>
    </AuthGuard>
  );
};

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
    errorElement: <ErrorComponent />,
  },
  {
    path: '/authguard_redirect',
    element: <AuthGuardRedirect />,
    errorElement: <ErrorComponent />,
  },
  {
    path: '/',
    element: <ProtectedRoute element={<Layout />} />,
    errorElement: <ErrorComponent />,
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <Dashboard />
          </ErrorBoundary>
        )
      },
      {
        path: 'start-process',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <StartProcess />
          </ErrorBoundary>
        )
      },
      {
        path: 'client-setup',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <ClientKeywordSetupScreen />
          </ErrorBoundary>
        )
      },
      {
        path: 'workflow/:clientId/:workflowId',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <WorkflowDashboard />
          </ErrorBoundary>
        )
      },
      {
        path: 'client-selection',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <ClientSelection />
          </ErrorBoundary>
        )
      },
      {
        path: 'module1',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module1AnalysisWrapper />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module1AnalysisWrapper />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module1AnalysisWrapper />
              </ErrorBoundary>
            )
          },
          {
            path: ':workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module1AnalysisWrapper />
              </ErrorBoundary>
            )
          },
          // Legacy URL format redirect for external IDs
          {
            path: 'external/:clientCode/:externalId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <WorkflowRedirect />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'module2',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module2Classification />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module2Classification />
              </ErrorBoundary>
            )
          },
          // Legacy URL format redirect for external IDs
          {
            path: 'external/:clientCode/:externalId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <WorkflowRedirect />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'module2-legacy',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module2CodeGeneration />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module2CodeGeneration />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'module3',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module3SemanticAnalysis />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module3SemanticAnalysis />
              </ErrorBoundary>
            )
          },
          // Legacy URL format redirect for external IDs
          {
            path: 'external/:clientCode/:externalId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <WorkflowRedirect />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'module4',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module4Config />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Module4Config />
              </ErrorBoundary>
            )
          },
          // Legacy URL format redirect for external IDs
          {
            path: 'external/:clientCode/:externalId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <WorkflowRedirect />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'api-debug',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <ApiDebugPage />
          </ErrorBoundary>
        )
      },
      {
        path: 'test-formats',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <TestAnalysisResults />
          </ErrorBoundary>
        )
      },
      {
        path: 'debug-formats',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <DebugAnalysisResults />
          </ErrorBoundary>
        )
      },
      {
        path: 'user-test',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <UserTest />
          </ErrorBoundary>
        )
      },
      {
        path: 'manage',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <Manage />
          </ErrorBoundary>
        )
      },
      {
        path: 'user-management',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <UserManagement />
          </ErrorBoundary>
        )
      },
      {
        path: 'application-settings',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <ApplicationSettings />
          </ErrorBoundary>
        )
      },
      {
        path: 'workflow2/:clientId/:workflowId',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <WorkflowV2Layout />
          </ErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: <FileUploadStep />
          },
          {
            path: 'keyword',
            element: <KeywordAnalysisStep />
          },
          {
            path: 'semantic',
            element: <SemanticAnalysisStep />
          },
          {
            path: 'batch_config',
            element: <BatchConfigStep />
          },
          {
            path: 'batch_run',
            element: <BatchRunStep />
          },
          {
            path: 'serp_tool',
            element: <SerpToolStep />
          },
          {
            path: 'data_for_seo',
            element: <DataForSeoStep />
          },
          {
            path: 'summary',
            element: <WorkflowSummaryStep />
          },
          {
            path: 'explore',
            element: <ExploreStep />
          }
        ]
      },
      {
        path: 'serp-tool',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <SerpTool />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <SerpTool />
              </ErrorBoundary>
            )
          },
          {
            path: ':workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <SerpTool />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'data-for-seo-tool',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <DataForSeoTool />
              </ErrorBoundary>
            )
          },
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <DataForSeoTool />
              </ErrorBoundary>
            )
          },
          {
            path: ':workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <DataForSeoTool />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'interactive-report/:batchId?',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <InteractiveReport />
          </ErrorBoundary>
        )
      },
      {
        path: 'explore',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Explore />
              </ErrorBoundary>
            )
          },
          // Gate route: loads first, redirects internally to /view when ready
          {
            path: 'client/:clientId/:workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <ExploreGate />
              </ErrorBoundary>
            )
          },
          // Real Explore view after readiness is confirmed
          {
            path: 'client/:clientId/:workflowId/view',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Explore />
              </ErrorBoundary>
            )
          },
          {
            path: ':workflowId',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <Explore />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'audit-demo',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <FileProgressAuditDemo />
          </ErrorBoundary>
        )
      },
      {
        path: 'sampling',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <SamplingDemoLanding />
              </ErrorBoundary>
            )
          },
          {
            path: 'demo',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <SamplingStrategyDemo />
              </ErrorBoundary>
            )
          },
          {
            path: 'comparison',
            element: (
              <ErrorBoundary fallback={<ErrorComponent />}>
                <SamplingComparisonDemo />
              </ErrorBoundary>
            )
          }
        ]
      },
      {
        path: 'sampling-demo',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <SamplingStrategyDemo />
          </ErrorBoundary>
        )
      },
      {
        path: 'sampling-comparison',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <SamplingComparisonDemo />
          </ErrorBoundary>
        )
      },
      // DISABLED: Qlik routes - no longer needed
      // {
      //   path: 'qlik-test',
      //   element: (
      //     <ErrorBoundary fallback={<ErrorComponent />}>
      //       <QlikTestPage />
      //     </ErrorBoundary>
      //   )
      // },
      // {
      //   path: 'minimal-qlik-test',
      //   element: (
      //     <ErrorBoundary fallback={<ErrorComponent />}>
      //       <MinimalQlikTest />
      //     </ErrorBoundary>
      //   )
      // },
      {
        path: 'jobs/:jobId',
        element: (
          <ErrorBoundary fallback={<ErrorComponent />}>
            <Module5JobMonitoring />
          </ErrorBoundary>
        )
      },
      {
        path: '*',
        element: <Navigate to="/" replace />
      }
    ]
  }
]);

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
